// assets/AllCourses/Exercises/MultipleChoiceExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function MultipleChoiceExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false, // ⚠️ IMPORTANT: match parent prop
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.multipleChoiceQuestions)
    ? exercise.multipleChoiceQuestions
    : [];

  /* ================= STATE ================= */
  const [answers, setAnswers] = useState({});
  const completedRef = useRef(false);

  /* ================= RESET ON RETRY ================= */
  useEffect(() => {
    if (!exercise?._id) return;

    setAnswers({});
    completedRef.current = false;
  }, [exercise._id]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text).trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) {
      onUpdate("multipleChoiceQuestions", next);
    }
  };

  const updateQuestionField = (idx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[idx] || {
      question: "",
      options: [],
      answer: "",
      requireCapitalization: false,
    };

    next[idx] = { ...current, [field]: value };
    updateQuestions(next);
  };

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (qIdx, value) => {
    if (isEditMode || showResult) return;

    setAnswers((prev) => {
      const next = {
        ...prev,
        [qIdx]: value,
      };

      // 🔥 ALWAYS update parent (for submit enable)
      onComplete(next, false);

      // check if all answered
      const allAnswered = questions.every(
        (_, i) => next[i] !== undefined
      );

      if (allAnswered) {
        checkAnswers(next);
      }

      return next;
    });
  };

  /* ================= CHECK ANSWERS ================= */
  const checkAnswers = (currentAnswers) => {
    let correctCount = 0;

    questions.forEach((q, idx) => {
      const user = currentAnswers[idx] || "";

      const isCorrect =
        normalize(user, q.requireCapitalization) ===
        normalize(q.answer, q.requireCapitalization);

      if (isCorrect) correctCount++;
    });

    const percent =
      questions.length > 0
        ? Math.round((correctCount / questions.length) * 100)
        : 0;

    // ✅ FINAL RESULT
    onComplete(currentAnswers, percent >= 50);
  };

  /* ================= EDIT MODE UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6">
            <div className="space-y-6">
              <TextareaField
                label={`Question ${qIdx + 1}`}
                value={q.question || ""}
                onChange={(v) =>
                  updateQuestionField(qIdx, "question", v)
                }
                rows={3}
                required
              />

              <div className="space-y-3">
                <Label>Options</Label>

                {q.options.map((opt, optIdx) => (
                  <div key={optIdx} className="flex gap-3">
                    <StringField
                      value={opt}
                      onChange={(v) => {
                        const next = [...questions];
                        next[qIdx].options[optIdx] = v;
                        updateQuestions(next);
                      }}
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() =>
                        updateQuestions(
                          questions.map((qq, i) =>
                            i === qIdx
                              ? {
                                  ...qq,
                                  options: qq.options.filter(
                                    (_, oi) => oi !== optIdx
                                  ),
                                }
                              : qq
                          )
                        )
                      }
                    >
                      Remove
                    </Button>
                  </div>
                ))}

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    updateQuestions(
                      questions.map((qq, i) =>
                        i === qIdx
                          ? {
                              ...qq,
                              options: [...qq.options, ""],
                            }
                          : qq
                      )
                    )
                  }
                >
                  Add Option
                </Button>
              </div>

              <StringField
                label="Correct Answer"
                value={q.answer || ""}
                onChange={(v) =>
                  updateQuestionField(qIdx, "answer", v)
                }
                required
              />

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) =>
                  updateQuestionField(
                    qIdx,
                    "requireCapitalization",
                    c
                  )
                }
              />
            </div>
          </Card>
        ))}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              {
                question: "",
                options: [],
                answer: "",
                requireCapitalization: false,
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No questions available
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT MODE ================= */
  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Multiple Choice</CardTitle>
        <CardDescription>
          Choose the correct answer
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {questions.map((q, qIdx) => {
          const selected = answers[qIdx];

          return (
            <div
              key={qIdx}
              className="rounded-xl border bg-muted/40 p-6 space-y-4"
            >
              <p className="font-medium">{q.question}</p>

              <RadioGroup
                value={selected ?? ""}
                onValueChange={(v) =>
                  handleChange(qIdx, v)
                }
                disabled={showResult}
                className="space-y-3"
              >
                {q.options.map((opt, optIdx) => {
                  const isSelected = selected === opt;

                  const correct =
                    normalize(opt, q.requireCapitalization) ===
                    normalize(q.answer, q.requireCapitalization);

                  return (
                    <div
                      key={optIdx}
                      className={cn(
                        "flex items-center gap-3 rounded-lg border p-4 transition",

                        // before submit
                        isSelected && !showResult && "bg-accent",

                        // after submit correct
                        showResult && correct && "bg-green-100 border-green-400",

                        // after submit wrong
                        showResult &&
                          isSelected &&
                          !correct &&
                          "bg-red-100 border-red-400"
                      )}
                    >
                      <RadioGroupItem
                        value={opt}
                        id={`mc-${qIdx}-${optIdx}`}
                      />
                      <Label
                        htmlFor={`mc-${qIdx}-${optIdx}`}
                        className="flex-1 cursor-pointer"
                      >
                        {opt}

                        {showResult && isSelected && !correct && (
                          <span className="ml-2 text-sm">
                            (Correct: {q.answer})
                          </span>
                        )}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}